import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { downloadBlob } from "../nativeDownload";

const MATCH_PLAY_FORMATS = ['Singles Match Play','Better Ball Match Play','Fourball Match Play','Foursomes Match Play','Greensomes Match Play'];

export default function Archive() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [matchResults, setMatchResults] = useState([]);
  const [twos, setTwos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useEffect(() => { loadEvents(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadEvents = async () => {
    try {
      const res = await api.get("/archive");
      setEvents(res.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const isMatchPlayEvent = (event) => MATCH_PLAY_FORMATS.includes(event?.game_type);

  const loadResults = async (event) => {
    setSelectedEvent(event);
    setResults([]);
    setMatchResults([]);
    setTwos([]);
    try {
      if (isMatchPlayEvent(event)) {
        const res = await api.get("/archive/" + event.id + "/matches");
        setMatchResults(res.data);
      } else {
        const res = await api.get("/archive/" + event.id + "/results");
        setResults(res.data);
      }
      const twosRes = await api.get("/archive/" + event.id + "/twos");
      setTwos(twosRes.data || []);
    } catch(e) { console.error(e); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this archived event?")) return;
    try {
      await api.delete("/archive/" + id);
      setEvents(prev => prev.filter(e => e.id !== id));
      if (selectedEvent?.id === id) { setSelectedEvent(null); setResults([]); setMatchResults([]); }
      showToast("Deleted");
    } catch(e) { showToast("Failed"); }
  };

  const isMedal = selectedEvent?.game_type?.includes("Medal") || selectedEvent?.game_type?.includes("Stroke");
  const isMatchPlay = isMatchPlayEvent(selectedEvent);
  const isInterClub = isMatchPlay && !!selectedEvent?.team_a_name;
  const winningClub = isInterClub
    ? (selectedEvent.team_a_wins > selectedEvent.team_b_wins ? selectedEvent.team_a_name
       : selectedEvent.team_b_wins > selectedEvent.team_a_wins ? selectedEvent.team_b_name
       : 'Halved')
    : null;

  const matchResultText = (m) => {
    const winnerLabel = m.winning_side === 'A' ? (isInterClub ? selectedEvent.team_a_name : m.team_a_players)
      : m.winning_side === 'B' ? (isInterClub ? selectedEvent.team_b_name : m.team_b_players)
      : null;
    return m.margin === 'Halved' || m.margin === 'All Square'
      ? m.margin
      : (winnerLabel ? `${winnerLabel} wins ${m.margin}` : (m.margin || '-'));
  };

  const exportToExcel = () => {
    if (!selectedEvent) { showToast("Select an event first"); return; }
    const wb = XLSX.utils.book_new();
    const eventDate = selectedEvent.date ? new Date(selectedEvent.date).toLocaleDateString('en-GB') : '-';
    const headerRows = [
      [selectedEvent.name || 'Event'],
      ["Format:", selectedEvent.game_type || ''],
      ["Date:", eventDate],
      ["Venue:", selectedEvent.venue || ''],
      ["Course:", selectedEvent.course_name || ''],
      []
    ];
    const prizes = selectedEvent || {};
    const prizeRows = [];
    if (prizes.nearest_pin || prizes.nearest_pin2 || prizes.longest_drive) {
      prizeRows.push([]);
      if (prizes.nearest_pin) prizeRows.push(["Nearest Pin:", prizes.nearest_pin]);
      if (prizes.nearest_pin2) prizeRows.push(["Nearest Pin 2:", prizes.nearest_pin2]);
      if (prizes.longest_drive) prizeRows.push(["Longest Drive:", prizes.longest_drive]);
    }

    const twosRows = [];
    if (twos.length > 0) {
      twosRows.push([]);
      twosRows.push(["Twos:"]);
      twos.forEach(t => twosRows.push([t.player_name, "Hole " + t.hole]));
    }
    if (isMatchPlay) {
      const aoa = [...headerRows];
      if (isInterClub) {
        aoa.push(["Final Score:", `${selectedEvent.team_a_name} ${selectedEvent.team_a_wins} - ${selectedEvent.team_b_wins} ${selectedEvent.team_b_name}${selectedEvent.matches_halved > 0 ? ` (${selectedEvent.matches_halved} halved)` : ''}`]);
        aoa.push([]);
      }
      aoa.push(["Match", "Team A", "Team B", "Result"]);
      matchResults.forEach(m => {
        aoa.push([m.grp, m.team_a_players, m.team_b_players, matchResultText(m)]);
      });
      aoa.push(...prizeRows);
      aoa.push(...twosRows);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, "Match Results");
    } else {
      const aoa = [...headerRows];
      aoa.push(["Pos", "Player", "HCP", "Gross", "Net", "Pts"]);
      results.forEach(r => {
        aoa.push([r.rank, r.player_name, r.handicap, r.gross, r.net, r.points]);
      });
      aoa.push(...prizeRows);
      aoa.push(...twosRows);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, "Results");
    }
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${selectedEvent.name}_${selectedEvent.game_type}`.replace(/\s+/g, '_') + ".xlsx");
  };

  const exportToPDF = () => {
    if (!selectedEvent) { showToast("Select an event first"); return; }
    const doc = new jsPDF();
    let y = 20;

    doc.setFontSize(16);
    doc.text(selectedEvent.name, 14, y);
    y += 8;
    doc.setFontSize(11);
    doc.text(`Format: ${selectedEvent.game_type}`, 14, y);
    y += 6;
    doc.text(`Date: ${selectedEvent.date ? new Date(selectedEvent.date).toLocaleDateString('en-GB') : '-'}`, 14, y);
    y += 6;
    doc.text(`Venue: ${selectedEvent.venue || '-'}`, 14, y);
    y += 6;
    doc.text(`Course: ${selectedEvent.course_name || '-'}`, 14, y);
    y += 10;

    const prizes = selectedEvent || {};
    const printPrizes = () => {
      if (!(prizes.nearest_pin || prizes.nearest_pin2 || prizes.longest_drive || twos.length > 0)) return;
      y += 6;
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text("Prizes", 14, y);
      doc.setFont(undefined, 'normal');
      y += 7;
      if (prizes.nearest_pin) { doc.text(`Nearest Pin: ${prizes.nearest_pin}`, 14, y); y += 6; }
      if (prizes.nearest_pin2) { doc.text(`Nearest Pin 2: ${prizes.nearest_pin2}`, 14, y); y += 6; }
      if (prizes.longest_drive) { doc.text(`Longest Drive: ${prizes.longest_drive}`, 14, y); y += 6; }
      twos.forEach(t => { if (y > 270) { doc.addPage(); y = 20; } doc.text(`Two: ${t.player_name} (Hole ${t.hole})`, 14, y); y += 6; });
    };

    if (isMatchPlay) {
      if (isInterClub) {
        doc.setFontSize(13);
        doc.text(`Final Result: ${selectedEvent.team_a_name} ${selectedEvent.team_a_wins} - ${selectedEvent.team_b_wins} ${selectedEvent.team_b_name}`, 14, y);
        y += 7;
        doc.text(`Winning Club: ${winningClub}`, 14, y);
        y += 10;
      }
      doc.setFontSize(13);
      doc.text("Match Results", 14, y);
      y += 6;
      doc.line(14, y, 80, y);
      y += 8;
      doc.setFontSize(11);
      matchResults.forEach(m => {
        if (y > 280) { doc.addPage(); y = 20; }
        doc.text(`${m.team_a_players} vs ${m.team_b_players} — ${matchResultText(m)}`, 14, y);
        y += 8;
      });
      printPrizes();
    } else {
      doc.setFontSize(10);
      const posX = 14, playerX = 30, hcpX = 120, grossX = 145, netX = 165, ptsX = 185;
      doc.setFont(undefined, 'bold');
      doc.text("Pos", posX, y);
      doc.text("Player", playerX, y);
      doc.text("HCP", hcpX, y);
      doc.text("Gross", grossX, y);
      doc.text("Net", netX, y);
      doc.text("Pts", ptsX, y);
      doc.setFont(undefined, 'normal');
      y += 6;
      doc.line(14, y, 196, y);
      y += 6;
      results.forEach(r => {
        if (y > 280) { doc.addPage(); y = 20; }
        doc.text(String(r.rank ?? '-'), posX, y);
        doc.text(r.player_name || '', playerX, y);
        doc.text(String(r.handicap ?? '-'), hcpX, y);
        doc.text(String(r.gross ?? '-'), grossX, y);
        doc.text(String(r.net ?? '-'), netX, y);
        doc.text(String(r.points ?? '-'), ptsX, y);
        y += 7;
      });
      printPrizes();
    }

    downloadBlob(doc.output("blob"), `${selectedEvent.name}_${selectedEvent.game_type}`.replace(/\s+/g, '_') + ".pdf");
  };

  return (
    <Layout>
      {toast && <div style={{position:"fixed",bottom:"20px",right:"20px",zIndex:9999}} className="bg-green-600 text-white px-4 py-3 rounded shadow-lg text-sm font-medium">{toast}</div>}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Competition Archive</span>
          <span className="text-xs text-gray-500">Click an event to view results</span>
          {selectedEvent && (
            <div className="ml-auto flex items-center gap-2">
              <button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📊 Export Excel</button>
              <button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded">📄 Export PDF</button>
            </div>
          )}
        </div>

        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-auto" style={{maxHeight:"35%"}}>
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-green-800 text-white">
                <tr>
                  <th className="px-4 py-2 text-left">Competition</th>
                  <th className="px-4 py-2 text-left">Format</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Venue</th>
                  <th className="px-4 py-2 text-left">Course</th>
                  <th className="px-4 py-2 text-center w-20">Delete</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e,i) => (
                  <tr key={e.id}
                    onClick={() => loadResults(e)}
                    className={"border-b border-gray-200 cursor-pointer hover:bg-yellow-50 " + (selectedEvent?.id===e.id ? "bg-yellow-100" : i%2===0 ? "bg-white" : "bg-green-50")}>
                    <td className="px-4 py-2 font-medium">{e.name}</td>
                    <td className="px-4 py-2 text-xs text-blue-600">{e.game_type}</td>
                    <td className="px-4 py-2">{e.date ? new Date(e.date).toLocaleDateString('en-GB') : "-"}</td>
                    <td className="px-4 py-2">{e.venue}</td>
                    <td className="px-4 py-2">{e.course_name}</td>
                    <td className="px-4 py-2 text-center">
                      <button onClick={ev => { ev.stopPropagation(); deleteEvent(e.id); }}
                        className="text-red-500 hover:text-red-700 text-xs font-semibold">Del</button>
                    </td>
                  </tr>
                ))}
                {!loading && events.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-gray-500">No archived events yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-300 flex-1 overflow-auto">
            {isMatchPlay ? (
              <>
                <div className="bg-gray-50 px-4 py-3 text-sm border-b border-gray-200">
                  <div className="font-semibold text-gray-800">Competition: {selectedEvent.name}</div>
                  <div className="text-gray-600">Format: {selectedEvent.game_type}</div>
                  {isInterClub ? (
                    <>
                      <div className="text-gray-600">
                        Final Result: {selectedEvent.team_a_name} {selectedEvent.team_a_wins} - {selectedEvent.team_b_wins} {selectedEvent.team_b_name}
                        {selectedEvent.matches_halved > 0 && <span> ({selectedEvent.matches_halved} halved)</span>}
                      </div>
                      <div className="font-semibold text-green-700">Winning Club: {winningClub}</div>
                    </>
                  ) : (
                    <div className="text-gray-500 text-xs">Individual event — no club result recorded</div>
                  )}
                  {(selectedEvent.nearest_pin || selectedEvent.nearest_pin2 || selectedEvent.longest_drive || twos.length > 0) && (
                    <div className="flex gap-6 flex-wrap mt-2">
                      {selectedEvent.nearest_pin && <span className="text-green-700 font-semibold text-xs">📍 Nearest Pin: {selectedEvent.nearest_pin}</span>}
                      {selectedEvent.nearest_pin2 && <span className="text-green-700 font-semibold text-xs">📍 Nearest Pin 2: {selectedEvent.nearest_pin2}</span>}
                      {selectedEvent.longest_drive && <span className="text-blue-700 font-semibold text-xs">🏌️ Longest Drive: {selectedEvent.longest_drive}</span>}
                      {twos.length > 0 && <span className="text-purple-700 font-semibold text-xs">🎯 Twos: {twos.map(t => t.player_name + " (Hole " + t.hole + ")").join(", ")}</span>}
                    </div>
                  )}
                </div>
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-green-800 text-white">
                    <tr>
                      <th className="px-4 py-2 text-center w-16">Match</th>
                      <th className="px-4 py-2 text-left">Team A</th>
                      <th className="px-4 py-2 text-left">Team B</th>
                      <th className="px-4 py-2 text-left">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchResults.map((m,i) => (
                      <tr key={m.id} className={"border-b border-gray-200 " + (i%2===0?"bg-white":"bg-green-50")}>
                        <td className="px-4 py-2 text-center font-bold">{m.grp}</td>
                        <td className="px-4 py-2">{m.team_a_players}</td>
                        <td className="px-4 py-2">{m.team_b_players}</td>
                        <td className="px-4 py-2 font-bold text-blue-700">{matchResultText(m)}</td>
                      </tr>
                    ))}
                    {selectedEvent && matchResults.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-6 text-gray-500">No match results found.</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            ) : (
              <>
                <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 border-b border-gray-200 flex gap-6 flex-wrap">
                  <span>{selectedEvent ? selectedEvent.name + " — " + selectedEvent.game_type : "Select an event above to view results"}</span>
                  {selectedEvent?.nearest_pin && <span className="text-green-700 font-semibold">📍 Nearest Pin: {selectedEvent.nearest_pin}</span>}
                  {selectedEvent?.nearest_pin2 && <span className="text-green-700 font-semibold">📍 Nearest Pin 2: {selectedEvent.nearest_pin2}</span>}
                  {selectedEvent?.longest_drive && <span className="text-blue-700 font-semibold">🏌️ Longest Drive: {selectedEvent.longest_drive}</span>}
                  {twos.length > 0 && <span className="text-purple-700 font-semibold">🎯 Twos: {twos.map(t => t.player_name + " (Hole " + t.hole + ")").join(", ")}</span>}
                </div>
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 bg-green-800 text-white">
                    <tr>
                      <th className="px-4 py-2 text-center w-12">Pos</th>
                      <th className="px-4 py-2 text-left">Player</th>
                      <th className="px-4 py-2 text-center w-16">HCP</th>
                      <th className="px-4 py-2 text-center w-16">Gross</th>
                      <th className="px-4 py-2 text-center w-16">Net</th>
                      {!isMedal && <th className="px-4 py-2 text-center w-16">Pts</th>}

                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r,i) => (
                      <tr key={r.id} className={"border-b border-gray-200 " + (i%2===0?"bg-white":"bg-green-50")}>
                        <td className="px-4 py-2 text-center font-bold">{r.rank}</td>
                        <td className="px-4 py-2">{r.player_name}</td>
                        <td className="px-4 py-2 text-center">{r.handicap}</td>
                        <td className="px-4 py-2 text-center">{r.gross}</td>
                        <td className="px-4 py-2 text-center">{r.net}</td>
                        {!isMedal && <td className="px-4 py-2 text-center font-bold text-blue-700">{r.points}</td>}

                      </tr>
                    ))}
                    {selectedEvent && results.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-6 text-gray-500">No results found.</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
